import React, { useRef, useState } from "react";
import emailjs from "@emailjs/browser";

// Utility: generate days of a month
function getCalendarDays(year, month) {
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const days = [];
  for (let i = 0; i < firstDay; i++) days.push(null);
  for (let d = 1; d <= daysInMonth; d++) days.push(d);
  return days;
}

export default function BookingWizard() {
  const today = new Date();
  const tomorrow = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate() + 1
  );

  const [step, setStep] = useState(1);
  const [year, setYear] = useState(tomorrow.getFullYear());
  const [month, setMonth] = useState(tomorrow.getMonth());
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState("");
  const [isSending, setIsSending] = useState(false);

  const formRef = useRef();

  const monthNames = [
    "January","February","March","April","May","June",
    "July","August","September","October","November","December"
  ];

  const timeSlots = [
    "11:00 AM","12:00 PM","1:00 PM",
    "2:00 PM","3:00 PM","4:00 PM",
  ];

  const calendarDays = getCalendarDays(year, month);

  const isDisabledDate = (day) => {
    const d = new Date(year, month, day);
    const isWeekend = d.getDay() === 0 || d.getDay() === 6;
    const isBeforeTomorrow = d < tomorrow;
    return isWeekend || isBeforeTomorrow;
  };

  const goPrevMonth = () => {
    if (month === 0) {
      setMonth(11);
      setYear(year - 1);
    } else setMonth(month - 1);
  };

  const goNextMonth = () => {
    if (month === 11) {
      setMonth(0);
      setYear(year + 1);
    } else setMonth(month + 1);
  };

  const handleNext = () => {
    if (step === 1 && !selectedDate) return;
    if (step === 2 && !selectedTime) return;
    setStep(step + 1);
  };

  const handleBack = () => setStep(step - 1);

  const sendEmail = (e) => {
    e.preventDefault();
    setIsSending(true);

    const businessInfo = formRef.current.business_info?.value || "Not provided";

    formRef.current.message.value =
      `Call booking request from Savvytron website.\n\nBusiness Info:\n${businessInfo}`;

    emailjs
      .sendForm(
        "service_uusknan",
        "template_zbfegom",
        formRef.current,
        "U3I-mFfvkUs_epjHx"
      )
      .then(
        () => {
          alert("Booking request sent successfully!");
          formRef.current.reset();
          setStep(1);
          setSelectedDate(null);
          setSelectedTime("");
          setIsSending(false);
        },
        () => {
          alert("Failed to send message. Try again later.");
          setIsSending(false);
        }
      );
  };

  return (
    <div className="grid lg:grid-cols-2 gap-10 w-full max-w-5xl mx-auto py-10 px-4">

      {/* LEFT CONTENT */}
      <div
        className="
          flex flex-col justify-center
          mb-8
          lg:mb-0 lg:pr-10 
          lg:border-r lg:border-gray-200
        "
      >
        <h2 className="text-3xl font-copperplate text-[#785f14] mb-4 text-left">
          Book a Free Call
        </h2>

        <p className="text-gray-600 leading-relaxed text-lg text-left">
          Select a date and time that works for you. I’ll personally guide you through
          how Savvytron can help your business go digital — affordably and professionally.
        </p>

        <div className="mt-6 space-y-4 text-gray-700 text-left">
          <div className="flex items-center gap-3">
            <span className="w-3 h-3 bg-[#785f14] rounded-full"></span>
            <p>30-minute strategy session</p>
          </div>

          <div className="flex items-center gap-3">
            <span className="w-3 h-3 bg-[#785f14] rounded-full"></span>
            <p>Ask anything about websites, apps, automation</p>
          </div>

          <div className="flex items-center gap-3">
            <span className="w-3 h-3 bg-[#785f14] rounded-full"></span>
            <p>No pressure — just honest guidance</p>
          </div>
        </div>
      </div>

      {/* RIGHT SIDE — CALENDAR WIZARD */}
      <div
        className="
          w-full bg-white rounded-2xl shadow-xl p-6 space-y-6
          lg:max-w-md lg:ml-auto
        "
      >

        {/* STEP 1 — DATE */}
        {step === 1 && (
          <>
            <h2 className="text-xl font-bold text-[#785f14] text-center">
              Select a Date
            </h2>

            <div className="flex justify-between items-center my-4 bg-gray-50 p-3 rounded-xl shadow-sm">
              <button
                onClick={goPrevMonth}
                className="px-3 py-1 rounded-md bg-white border hover:bg-gray-100"
              >
                ←
              </button>
              <p className="text-lg font-semibold text-[#785f14]">
                {monthNames[month]} {year}
              </p>
              <button
                onClick={goNextMonth}
                className="px-3 py-1 rounded-md bg-white border hover:bg-gray-100"
              >
                →
              </button>
            </div>

            {/* Weekdays */}
            <div className="grid grid-cols-7 gap-2 text-center text-gray-500 font-semibold text-sm">
              <div>Sun</div><div>Mon</div><div>Tue</div>
              <div>Wed</div><div>Thu</div><div>Fri</div><div>Sat</div>
            </div>

            {/* Calendar */}
            <div className="grid grid-cols-7 gap-2">
              {calendarDays.map((day, i) =>
                day === null ? (
                  <div key={i}></div>
                ) : (
                  <button
                    key={i}
                    disabled={isDisabledDate(day)}
                    onClick={() =>
                      !isDisabledDate(day) &&
                      setSelectedDate(new Date(year, month, day))
                    }
                    className={`
                      p-2 rounded-lg border transition text-center
                      ${
                        isDisabledDate(day)
                          ? "bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200"
                          : selectedDate?.getDate() === day
                            ? "bg-[#785f14] text-white border-[#785f14] shadow"
                            : "bg-white border-gray-300 hover:border-[#785f14]"
                      }
                    `}
                  >
                    {day}
                  </button>
                )
              )}
            </div>

            <div className="flex justify-end">
              <button
                onClick={handleNext}
                className="mt-6 py-3 px-6 bg-[#785f14] text-white rounded-lg hover:bg-[#6b5312] font-semibold"
              >
                Next →
              </button>
            </div>
          </>
        )}

        {/* STEP 2 — TIME */}
        {step === 2 && (
          <>
            <h2 className="text-xl font-bold text-[#785f14] text-center">
              Select a Time
            </h2>

            <div className="grid grid-cols-2 gap-3 mt-4">
              {timeSlots.map((slot) => (
                <button
                  key={slot}
                  onClick={() => setSelectedTime(slot)}
                  className={`
                    p-3 rounded-lg border transition
                    ${
                      selectedTime === slot
                        ? "bg-[#785f14] text-white border-[#785f14] shadow"
                        : "bg-white border-gray-300 hover:border-[#785f14]"
                    }
                  `}
                >
                  {slot}
                </button>
              ))}
            </div>

            <div className="flex justify-between mt-6">
              <button
                onClick={handleBack}
                className="py-3 px-6 border border-[#785f14] text-[#785f14] rounded-lg hover:bg-[#785f14]/10 font-semibold"
              >
                ← Back
              </button>

              <button
                onClick={handleNext}
                className="py-3 px-6 bg-[#785f14] text-white rounded-lg hover:bg-[#6b5312] font-semibold"
              >
                Next →
              </button>
            </div>
          </>
        )}

        {/* STEP 3 — INFO + EMAILJS */}
        {step === 3 && (
          <form ref={formRef} onSubmit={sendEmail} className="space-y-4">

            <h2 className="text-xl font-bold text-[#785f14] text-center">
              Your Information
            </h2>

            {/* Hidden fields */}
            <input
              type="hidden"
              name="preferred_time"
              value={
                selectedDate && selectedTime
                  ? `${selectedDate.toLocaleDateString("en-US", {
                      month: "long",
                      day: "numeric",
                      year: "numeric",
                    })} at ${selectedTime}`
                  : ""
              }
            />

            <input
              type="hidden"
              name="time"
              value={new Date().toLocaleString()}
            />

            <input type="hidden" name="message" />

            {/* Name */}
            <input
              type="text"
              name="user_name"
              placeholder="Full Name"
              required
              className="w-full p-3 rounded-lg border bg-gray-50 border-gray-300 focus:ring-2 focus:ring-[#785f14]/30"
            />

            {/* Email */}
            <input
              type="email"
              name="user_email"
              placeholder="Email Address"
              required
              className="w-full p-3 rounded-lg border bg-gray-50 border-gray-300 focus:ring-2 focus:ring-[#785f14]/30"
            />

            {/* Phone */}
            <input
              type="tel"
              name="user_phone"
              placeholder="Phone Number"
              className="w-full p-3 rounded-lg border bg-gray-50 border-gray-300 focus:ring-2 focus:ring-[#785f14]/30"
            />

            {/* Business Info */}
            <textarea
              name="business_info"
              placeholder="Tell me about your business…"
              rows={3}
              className="w-full p-3 rounded-lg border bg-gray-50 border-gray-300 focus:ring-2 focus:ring-[#785f14]/30"
            ></textarea>

            <div className="flex justify-between mt-6">
              <button
                type="button"
                onClick={handleBack}
                className="py-3 px-6 border border-[#785f14] text-[#785f14] rounded-lg hover:bg-[#785f14]/10 font-semibold"
              >
                ← Back
              </button>

              <button
                type="submit"
                disabled={isSending}
                className={`py-3 px-6 rounded-lg font-semibold text-white transition
                  ${
                    isSending
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-[#785f14] hover:bg-[#6b5312]"
                  }
                `}
              >
                {isSending ? "Sending..." : "Submit"}
              </button>
            </div>

          </form>
        )}
      </div>
    </div>
  );
}
